import { ReportComponent, ReportComponentType } from '@/types';
import { ReportComponentConfig, TableConfig } from '@/types/api/modules/business-intelligence';

import { normalizeReportComponentConfiguration } from './configuration';

type RequirementKey = 'dataSet' | 'query' | 'metrics' | 'dimensions' | 'filters' | 'dateRange' | 'columns';

export const COMPONENT_READINESS_RULES: Partial<Record<ReportComponentType, RequirementKey[]>> = {
    [ReportComponentType.BAR_CHART]: ['dataSet', 'query', 'metrics', 'dimensions'],
    [ReportComponentType.LINE_CHART]: ['dataSet', 'query', 'metrics', 'dimensions'],
    [ReportComponentType.AREA_CHART]: ['dataSet', 'query', 'metrics', 'dimensions'],
    [ReportComponentType.PIE_CHART]: ['dataSet', 'query', 'metrics', 'dimensions'],
    [ReportComponentType.DONUT_CHART]: ['dataSet', 'query', 'metrics', 'dimensions'],
    [ReportComponentType.FUNNEL_CHART]: ['dataSet', 'query', 'metrics'],
    [ReportComponentType.SCATTER_PLOT]: ['dataSet', 'query', 'metrics'],
    [ReportComponentType.HEAT_MAP]: ['dataSet', 'query', 'metrics', 'dimensions'],
    [ReportComponentType.SANKEY_CHART]: ['dataSet', 'query', 'metrics', 'dimensions'],
    [ReportComponentType.RADAR_CHART]: ['dataSet', 'query', 'metrics', 'dimensions'],
    [ReportComponentType.RADIAL_CHART]: ['dataSet', 'query', 'metrics', 'dimensions'],
    [ReportComponentType.GAUGE_CHART]: ['dataSet', 'query', 'metrics'],
    [ReportComponentType.TABLE]: ['dataSet', 'query'],
    [ReportComponentType.MAP]: ['dataSet', 'query', 'dimensions'],
    [ReportComponentType.GLOBE]: ['dataSet', 'query', 'metrics', 'dimensions'],
    [ReportComponentType.CHORD_CHART]: ['dataSet', 'query', 'metrics', 'dimensions'],
    [ReportComponentType.TITLE]: [],
    [ReportComponentType.DESCRIPTION]: [],
    [ReportComponentType.STATISTIC]: ['dataSet', 'query', 'metrics'],
    [ReportComponentType.MULTIPLE_STATISTICS]: ['dataSet', 'query', 'metrics'],
    [ReportComponentType.EMBED]: [],
};

export function evaluateComponentReadiness(component: ReportComponent): {
    isReady: boolean;
    missing: RequirementKey[];
} {
    const config = normalizeReportComponentConfiguration(
        component.configuration,
        component.type,
    ) as ReportComponentConfig;

    const query = config?.query;
    const missing: RequirementKey[] = [];
    const rules = COMPONENT_READINESS_RULES[component.type] ?? [];

    if (!rules.length) {
        return {
            isReady: true,
            missing: [],
        };
    }

    /**
     * -----------------------------
     * 1. Dataset requirement
     * -----------------------------
     */
    if (rules.includes('dataSet') && !config?.dataSet) {
        missing.push('dataSet');
    }

    /**
     * -----------------------------
     * 2. Query existence
     * -----------------------------
     */
    if (rules.includes('query') && !query) {
        missing.push('query');
    }

    /**
     * -----------------------------
     * 3. Metrics requirement
     * -----------------------------
     */
    const hasMetrics = !!query?.metrics && query.metrics.length > 0;
    const metricCount = query?.metrics?.length ?? 0;

    /**
     * -----------------------------
     * 4. Dimensions requirement
     * -----------------------------
     */
    const hasDimensions = !!query?.dimensions && query.dimensions.length > 0;
    const dimensionCount = query?.dimensions?.length ?? 0;

    /**
     * -----------------------------
     * 5. Component-specific rules
     * -----------------------------
     */
    switch (component.type) {
        case ReportComponentType.BAR_CHART:
        case ReportComponentType.LINE_CHART:
        case ReportComponentType.AREA_CHART:
        case ReportComponentType.PIE_CHART:
        case ReportComponentType.DONUT_CHART:
        case ReportComponentType.RADIAL_CHART: {
            if (!hasMetrics) {
                missing.push('metrics');
            }
            if (!hasDimensions) {
                missing.push('dimensions');
            }
            break;
        }

        case ReportComponentType.FUNNEL_CHART: {
            if (!hasMetrics) {
                missing.push('metrics');
            }
            break;
        }

        case ReportComponentType.SCATTER_PLOT: {
            if (metricCount < 2) {
                missing.push('metrics');
            }
            break;
        }

        case ReportComponentType.HEAT_MAP:
        case ReportComponentType.SANKEY_CHART:
        case ReportComponentType.CHORD_CHART: {
            if (!hasMetrics) {
                missing.push('metrics');
            }
            if (dimensionCount < 2) {
                missing.push('dimensions');
            }
            break;
        }

        case ReportComponentType.RADAR_CHART: {
            if (!hasMetrics) {
                missing.push('metrics');
            }
            if (!hasDimensions) {
                missing.push('dimensions');
            }
            break;
        }

        case ReportComponentType.GAUGE_CHART: {
            if (!hasMetrics) {
                missing.push('metrics');
            }
            break;
        }

        case ReportComponentType.STATISTIC:
        case ReportComponentType.MULTIPLE_STATISTICS: {
            if (!hasMetrics) {
                missing.push('metrics');
            }
            break;
        }

        case ReportComponentType.MAP: {
            if (dimensionCount < 2) {
                missing.push('dimensions');
            }
            break;
        }

        case ReportComponentType.GLOBE: {
            if (!hasMetrics) {
                missing.push('metrics');
            }
            if (!hasDimensions) {
                missing.push('dimensions');
            }
            break;
        }

        case ReportComponentType.TABLE: {
            const hasColumns = !!(config as TableConfig).columns?.length;

            // Tables can either define columns OR rely on query output
            if (!hasColumns && !hasDimensions && !hasMetrics) {
                missing.push('columns');
            }

            break;
        }

        case ReportComponentType.TITLE:
        case ReportComponentType.DESCRIPTION:
        case ReportComponentType.EMBED: {
            // No data requirements
            break;
        }

        default:
            break;
    }

    return {
        isReady: missing.length === 0,
        missing,
    };
}
